import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import Container from '@common/components/container';
import Overview from '@common/components/overview';
import { TopNavigationBar } from '@common/components/navigation-bar';
import DataTable from '@common/components/datatable';
import Placeholder from '@common/components/placeholder';
import Card from '@common/components/card';
import Icon from '@common/components/icon';
import useColumn from './useColumn';
import { useRequest } from '@common/services/api';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import { selected } from '@modules/organisation/selectors/organisation';
import { Bar } from '@common/components/bar';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { EPredicateFilters } from '@common/components/predicates-filter';
import {
  EPerformanceEvaluationStatus,
  EPredicateFields,
  EPredicateOperators,
} from '@common/definitions';
import { omit } from 'lodash';
import { EvaluationsMeta } from '@modules/performance/types';
import { DataTableRow } from '@common/components/datatable/types';
import { Button } from '@common/components/button';

const EvaluationsContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams<{ networkId: string }>();
  const organisation = useAppSelector(selected);
  const networks = useAppSelector(organisationSelectors.networks);
  const functions = useAppSelector(organisationSelectors.functions);
  const [search, setSearch] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(20);
  const didMount = useDidComponentMount();
  const users = useAppSelector((state) => Object.values(state.users.items));

  const {
    data: forms,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/forms`,
    {
      useCursor: true,
      limit: 100,
      payload: {},
      transform: (res: any) => {
        return {
          ...res,
        };
      },
    }
  );

  const basePath = params.networkId
    ? `/networks/${params.networkId}`
    : '/admin';

  const statusFilterOptions = [
    {
      label: t('performance:started'),
      value: EPerformanceEvaluationStatus.STARTED,
    },
    {
      label: t('performance:submitted'),
      value: EPerformanceEvaluationStatus.SUBMITTED,
    },
    {
      label: t('performance:completed'),
      value: EPerformanceEvaluationStatus.FINISHED,
    },
  ];

  const [predicates, setPredicates] = useState([
    {
      filter: EPredicateFilters[EPredicateFields.NETWORK],
      value: {
        attribute: EPredicateFields.NETWORK,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: networks || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.FUNCTION],
      value: {
        attribute: EPredicateFields.FUNCTION,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: functions || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_STATUS],
      value: {
        attribute: EPredicateFields.PERFORMANCE_EVALUATION_STATUS,
        comparison: EPredicateOperators.EQ,
        value: '',
      },
      options: statusFilterOptions,
    },
    {
      filter:
        EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_MANAGER],
      value: {
        attribute: EPredicateFields.PERFORMANCE_EVALUATION_MANAGER,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: users || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_FORM],
      value: {
        attribute: EPredicateFields.PERFORMANCE_EVALUATION_FORM,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: forms || [],
    },
  ]);

  const [network_ids, function_ids, status, user_id, form_id] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
      )?.value.value,
      predicates.find(
        (p) => p.filter ===
          EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_STATUS]
      )?.value.value,
      predicates.find(
        (p) => p.filter ===
          EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_MANAGER]
      )?.value.value,
      predicates.find(
        (p) => p.filter ===
          EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_FORM]
      )?.value.value,
    ];
  }, [predicates]);

  useEffect(() => {
    if (forms.length > 0) {
      setPredicates((prev) => prev.map((predicate) => {
          if (
            predicate.filter ===
            EPredicateFilters[EPredicateFields.PERFORMANCE_EVALUATION_FORM]
          ) {
            return {
              ...predicate,
              options: forms,
            };
          }
          return predicate;
        })
      );
    }
  }, [forms]);

  const payload = useMemo(
    () => ({
      ...(network_ids ? { networkIds: network_ids } : {}),
      ...(function_ids ? { functionIds: function_ids } : {}),
      ...(user_id ? { reviewerIds: user_id } : {}),
      ...(status ? { status } : {}),
      ...(search && search !== '' ? { q: search } : {}),
      ...(form_id ? { formIds: form_id } : {}),
    }),
    [network_ids, function_ids, search, status, user_id, form_id]
  );

  const apiConfig = useMemo(
    () => ({
      useCursor: true,
      limit: pageSize,
      payload,
      transform: (res: any) => {
        return {
          ...res,
        };
      },
    }),
    [payload, pageSize]
  );

  const {
    data: evaluations,
    meta: metaData,
    loading,
    refetch,
    reset,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/evaluations`,
    apiConfig
  );

  const columns = useColumn();

  const meta: EvaluationsMeta = metaData;

  const handleRowClick = (row: DataTableRow) => {
    history.push(`${basePath}/performance/evaluations/${row.id}`);
  };

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [network_ids, function_ids, search, status, user_id, form_id, didMount, reset]);

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [pageSize, didMount, reset]);

  return (
    <Container name="Evaluations" className="tw-max-w-[calc(100%-265px)]">
      <TopNavigationBar title={t('performance:evaluations')} />
      <Container.Content>
        <Bar className="tw-flex tw-flex-row tw-items-center">
          <FiltersToolbar
            predicates={predicates}
            setPredicates={setPredicates}
            organisation={organisation}
            onSearch={setSearch}
            searchPlaceholder={t('common:search_list_search_placeholder')}
          />
          <Button
            iconRight="start_evaluation"
            type="primary"
            iconSize={14}
            className="!tw-gap-2"
            onClick={() => history.push(`${basePath}/performance/bulk-evaluation`)}
          >
            {t('performance:start_evaluations')}
          </Button>
        </Bar>
        <div className="tw-flex tw-gap-4 tw-pb-4">
          <Card>
            <Card.Content className="!tw-p-5">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Icon
                  size={18}
                  type="file_02_bolt"
                  className="tw-text-blue-custom"
                  isFilled
                />
                <span className="tw-font-inter tw-font-medium tw-text-base tw-text-gray-text">
                  {t('performance:evaluations_started')}
                </span>
              </div>
              <div className="tw-pt-3 tw-font-inter tw-font-medium tw-text-6xl">
                {meta?.counts?.started || 0}
              </div>
            </Card.Content>
          </Card>
          <Card>
            <Card.Content className="!tw-p-5">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Icon
                  size={18}
                  type="file_02_arrow_down__filled"
                  className="tw-text-orange-custom"
                />
                <span className="tw-font-inter tw-font-medium tw-text-base tw-text-gray-text">
                  {t('performance:evaluations_submitted')}
                </span>
              </div>
              <div className="tw-pt-3 tw-font-inter tw-font-medium tw-text-6xl">
                {meta?.counts?.submitted || 0}
              </div>
            </Card.Content>
          </Card>
          <Card>
            <Card.Content className="!tw-p-5">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Icon
                  size={18}
                  type="file_02_check"
                  className="tw-text-green-custom"
                  isFilled
                />
                <span className="tw-font-inter tw-font-medium tw-text-base tw-text-gray-text">
                  {t('performance:evaluations_completed')}
                </span>
              </div>
              <div className="tw-pt-3 tw-font-inter tw-font-medium tw-text-6xl">
                {meta?.counts?.completed || 0}
              </div>
            </Card.Content>
          </Card>
        </div>
        <Overview>
          <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200">
            <DataTable
              columns={columns}
              data={evaluations}
              loading={loading}
              stickyHeader
              stickyFirstColumn
              onShowMore={refetch}
              onRowClick={handleRowClick}
              pagination={{
                ...omit(apiConfig, 'payload'),
                ...meta?.pagination,
                ...meta?.counts,
                mode: 'paginated',
              }}
              onPageChange={(newPage, newPageSize) => {
                // setCurrentPage(newPage);
                setPageSize(newPageSize);
              }}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                // setCurrentPage(0);
              }}
              emptyView={
                <Placeholder
                  title={t('performance:no_evaluations_placeholder')}
                />
              }
            />
          </div>
        </Overview>
      </Container.Content>
    </Container>
  );
};

export default EvaluationsContainer;
